import { useMemo, useCallback, useEffect } from "react";

import { Button } from "@kelp-dao/kernel-design-system";

import { toFixedWithoutRounding } from "@constants/common";
import useDepositPoolPaused from "@hooks/useDepositPoolPaused";
import { useAgEthDeposit } from "./Vault/AirdropVault/agEthHooks";
import { Text } from "@chakra-ui/react";
import { useVaultTvlApy } from "@hooks/useVaultTvlApy";
import { useRestakeApy } from "@hooks/useRestakeApy";
import { trackEvent } from "@lib/utils";
import { STAKE_WITH_GAIN_CTA } from "@constants/analytics";

const MODAL_STRINGS = {
  BUTTON_TEXT: "Deposit in Gain vault",
} as const;

interface StakeWithAgEthProps {
  amount: string;
  selectedToken: string;
  error: string;
  isConnected: boolean;
  isPoolSizeExceeded?: boolean;
  variant?: "primary" | "outline";
}

const StakeWithAgEth = ({
  amount,
  selectedToken,
  error,
  isConnected,
  isPoolSizeExceeded = false,
  variant = "primary",
}: StakeWithAgEthProps) => {
  const { handleClick, handleChange, handleTokenSelect, checkInput } =
    useAgEthDeposit();

  const { isPaused: isDepositPoolPaused } = useDepositPoolPaused();

  useEffect(() => {
    if (amount) {
      handleChange(amount);
    }
  }, [amount, handleChange]);

  const { smartAPY: apy, isLoading: isVaultTvlApyLoading } =
    useVaultTvlApy("agETH");
  const { calculatedTotalAPY } = useRestakeApy();

  const agETHApy =
    apy && calculatedTotalAPY
      ? (
          +toFixedWithoutRounding(apy.toString(), 2) - +calculatedTotalAPY
        ).toFixed(1)
      : null;

  const vaultError = useMemo(() => {
    return checkInput(amount);
  }, [amount, checkInput]);

  const isDisabled = useMemo(() => {
    return (
      Boolean(error) ||
      Boolean(vaultError) ||
      isDepositPoolPaused ||
      (isPoolSizeExceeded && isConnected) ||
      (!amount && isConnected)
    );
  }, [
    error,
    vaultError,
    isConnected,
    isPoolSizeExceeded,
    amount,
    isDepositPoolPaused,
  ]);

  const depositGainModalTitle = useMemo(
    () => (
      <div className="flex items-center gap-1">
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight={500}
          color="#1A1A1A"
          className="flex flex-wrap items-center gap-1"
        >
          <span>Deposit in Airdrop Vault</span>
          <span className="inline-flex items-center bg-[#DDEFF0] rounded-[8px] p-2">
            <Text
              fontSize="12px"
              lineHeight="14px"
              fontWeight={400}
              color="#1A1A1A"
            >
              Total APY: {apy}
            </Text>
          </span>
        </Text>
      </div>
    ),
    [apy]
  );

  const handleDepositConfirm = useCallback(() => {
    trackEvent(STAKE_WITH_GAIN_CTA, {
      vault_name: "airdrop",
      amount: amount,
    });
    handleClick(amount, depositGainModalTitle);
  }, [handleClick, amount, depositGainModalTitle]);

  // Sync selected token with hook when it changes
  useEffect(() => {
    if (selectedToken) {
      handleTokenSelect(selectedToken);
    }
  }, [selectedToken, handleTokenSelect]);

  return (
    <>
      {agETHApy !== null && !isVaultTvlApyLoading ? (
        <div className="flex flex-col gap-2">
          <Button
            variant={variant}
            className={`w-full py-3 mt-2 text-[12px] md:text-[14px] 2xl:text-[18px] ${
              isDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleDepositConfirm}
            disabled={isDisabled}
          >
            <div className="flex items-center gap-1">
              {MODAL_STRINGS.BUTTON_TEXT}

              <div
                className={`flex items-center rounded-[8px] py-[6px] px-1 ${
                  isDisabled ? "bg-[#9CA3AF]" : "bg-[#387B7B]"
                }`}
              >
                <Text
                  fontSize="14px"
                  lineHeight="18px"
                  fontWeight={400}
                  color="#FFF"
                >
                  +{agETHApy}% APR
                </Text>
              </div>
            </div>
          </Button>
          {amount && !error && vaultError ? (
            <Text
              fontSize="12px"
              lineHeight="16px"
              fontWeight={500}
              color="#E73F3F"
            >
              {vaultError}
            </Text>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default StakeWithAgEth;
